import { useEffect, useMemo } from "react"

export default function usePatientsRouteState({
  location,
  id,
  activeClinicId,
  activeClinic,
  routeClinicId,
  navigate,
  setActiveTab,
  setActiveFilter,
  setSelectedPatientId,
  loadPatientWorkspace,
  safeClinicId
}) {
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const clinicIdFromUrl = queryParams.get("clinicId") || ""
  const tabFromUrl = queryParams.get("tab") || ""
  const focusFromUrl = queryParams.get("focus") || ""

  useEffect(() => {
    setSelectedPatientId(id || "")
  }, [id, setSelectedPatientId])

  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl)
    } else {
      setActiveTab("SUMMARY")
    }
  }, [tabFromUrl, setActiveTab])

  useEffect(() => {
    const normalizedFocus = String(focusFromUrl || "").trim().toUpperCase()

    if (!normalizedFocus) return

    if (normalizedFocus === "RECALL") {
      setActiveFilter("RECALL")
      setActiveTab("SUMMARY")
      return
    }

    if (
      normalizedFocus === "PAYMENTS" ||
      normalizedFocus === "RECEIVABLES" ||
      normalizedFocus === "MANUAL_APPLY" ||
      normalizedFocus === "TIMELINE"
    ) {
      setActiveTab("FINANCE")
    }
  }, [focusFromUrl, setActiveFilter, setActiveTab])

  useEffect(() => {
    if (!id) return

    if (activeClinicId === "ALL") {
      setSelectedPatientId(null)
      navigate("/patients", { replace: true })
      return
    }

    if (activeClinic && routeClinicId && routeClinicId !== activeClinic) {
      const params = new URLSearchParams()
      params.set("clinicId", activeClinic)
      setSelectedPatientId(null)
      navigate(`/patients?${params.toString()}`, { replace: true })
    }
  }, [
    id,
    activeClinicId,
    activeClinic,
    routeClinicId,
    navigate,
    setSelectedPatientId
  ])

  useEffect(() => {
    const run = async () => {
      if (!id) return

      if (!activeClinicId || activeClinicId === "ALL") {
        setSelectedPatientId(null)
        return
      }

      if (activeClinic && routeClinicId && routeClinicId !== activeClinic) {
        setSelectedPatientId(null)
        return
      }

      const result = await loadPatientWorkspace(id, safeClinicId)

      if (!result?.ok && result?.reason === "PATIENT_NOT_IN_CLINIC") {
        setSelectedPatientId(null)
      }
    }

    run()
  }, [
    id,
    activeClinicId,
    activeClinic,
    routeClinicId,
    safeClinicId,
    loadPatientWorkspace,
    setSelectedPatientId
  ])

  return {
    clinicIdFromUrl,
    tabFromUrl,
    focusFromUrl
  }
}